import { Button, notify } from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import {
  WorkspaceAreaCacheStore,
  type WorkspaceAreaRef,
} from '@affine/core/modules/folder-api';
import { FolderShareModal } from '@affine/core/modules/folder-share';
import { TeamspaceNameDialog } from '@affine/core/modules/workspace-areas';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { FolderIcon } from '@blocksuite/icons/rc';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSettingsOfflineIncompleteLabel } from '../../shared/settings-offline-status';
import {
  SettingsActionRow,
  SettingsConfirmButton,
  SettingsContentSection,
  SettingsContentText,
  SettingsDataList,
  SettingsDataRow,
} from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
  SettingsExpandableItem,
  SettingsMetaItem,
} from '../shared/settings-entity';

export const WorkspaceTeamspacesPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceAreaCache = useService(WorkspaceAreaCacheStore);
  const areas = useLiveData(
    useMemo(
      () => LiveData.from(workspaceAreaCache.watchManageableAreas(), []),
      [workspaceAreaCache]
    )
  );
  const isPopulated = useLiveData(workspaceAreaCache.isPopulated$);
  const isLocalCacheLoaded = useLiveData(
    workspaceAreaCache.isLocalCacheLoaded$
  );
  const [error, setError] = useState<string | null>(null);
  const isLoading = backendAvailable && !isPopulated && !isLocalCacheLoaded;
  const refresh = useCallback(async () => {
    setError(null);
    if (!backendAvailable) {
      return;
    }
    try {
      await workspaceAreaCache.refresh();
    } catch (error) {
      setError(UserFriendlyError.fromAny(error).message);
    }
  }, [backendAvailable, workspaceAreaCache]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const teamspaces = useMemo(
    () =>
      areas
        .filter(area => area.kind === 'teamspace')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [areas]
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingArea, setEditingArea] = useState<WorkspaceAreaRef | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [shareArea, setShareArea] = useState<WorkspaceAreaRef | null>(null);

  const openCreate = useCallback(() => {
    setEditingArea(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((area: WorkspaceAreaRef) => {
    setEditingArea(area);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingArea(null);
  }, []);

  const saveModal = useCallback(
    async (name: string) => {
      if (editingArea) {
        await workspaceAreaCache.updateTeamspace(editingArea.rootFolderId, {
          name,
        });
      } else {
        await workspaceAreaCache.createTeamspace(name);
      }
      await refresh();
    },
    [editingArea, refresh, workspaceAreaCache]
  );

  const trashArea = useCallback(
    async (area: WorkspaceAreaRef) => {
      try {
        await workspaceAreaCache.trashTeamspace(area.rootFolderId);
        await refresh();
        if (expandedId === area.rootFolderId) {
          setExpandedId(null);
        }
      } catch (error) {
        console.error('Failed to trash teamspace:', error);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.teamspaces.trash-failed'
            ]?.() ?? 'Teamspace could not be moved to trash',
        });
      }
    },
    [expandedId, refresh, t, workspaceAreaCache]
  );

  return (
    <SettingsContentSection
      title={
        t['com.streamlain.settings.workspace.teamspaces.title']?.() ??
        'Teamspaces'
      }
    >
      <SettingsContentText>
        {t['com.streamlain.settings.workspace.teamspaces.description']?.() ??
          'Teamspaces are private workspace areas managed through folder permissions.'}
      </SettingsContentText>
      <SettingsActionRow>
        <Button
          data-testid="settings-teamspaces-create-button"
          onClick={openCreate}
          variant="primary"
          disabled={!backendAvailable}
        >
          {t['com.streamlain.settings.workspace.teamspaces.create']?.() ??
            'New teamspace'}
        </Button>
      </SettingsActionRow>
      {!backendAvailable && !isLocalCacheLoaded ? (
        <SettingsEntityMessage>{offlineLabel}</SettingsEntityMessage>
      ) : error ? (
        <SettingsEntityMessage>{error}</SettingsEntityMessage>
      ) : null}
      {isLoading ? (
        <SettingsEntityMessage>
          {t['com.streamlain.settings.member.loading']?.() ?? 'Loading...'}
        </SettingsEntityMessage>
      ) : teamspaces.length === 0 ? (
        <SettingsEntityMessage>
          {t['com.streamlain.settings.workspace.teamspaces.empty']?.() ??
            'No teamspaces yet.'}
        </SettingsEntityMessage>
      ) : (
        <SettingsEntityList>
          {teamspaces.map(area => {
            const canMutateArea = area.kind === 'teamspace' && !area.immutable;
            return (
              <SettingsExpandableItem
                key={area.rootFolderId}
                leading={<FolderIcon />}
                title={area.name}
                badges={
                  <>
                    <SettingsBadge>{area.kind}</SettingsBadge>
                    <SettingsBadge>{area.effectiveRole}</SettingsBadge>
                    <SettingsBadge>{area.visibility}</SettingsBadge>
                  </>
                }
                meta={
                  <>
                    <SettingsMetaItem>{area.rootFolderId}</SettingsMetaItem>
                    {area.immutable ? (
                      <SettingsMetaItem>immutable</SettingsMetaItem>
                    ) : null}
                  </>
                }
                expanded={expandedId === area.rootFolderId}
                onToggle={() =>
                  setExpandedId(
                    expandedId === area.rootFolderId ? null : area.rootFolderId
                  )
                }
                dataTestId={`settings-teamspace-${area.rootFolderId}`}
              >
                <SettingsDataList>
                  <SettingsDataRow label="Area" value={area.kind} />
                  <SettingsDataRow
                    label={
                      t[
                        'com.streamlain.settings.workspace.teamspaces.role'
                      ]?.() ?? 'Your role'
                    }
                    value={area.effectiveRole}
                  />
                  <SettingsDataRow
                    label={
                      t[
                        'com.streamlain.settings.workspace.teamspaces.visibility'
                      ]?.() ?? 'Visibility'
                    }
                    value={area.visibility}
                  />
                </SettingsDataList>
                <SettingsActionRow>
                  <Button onClick={() => setShareArea(area)}>
                    {t[
                      'com.streamlain.settings.workspace.teamspaces.permissions'
                    ]?.() ?? 'Manage permissions'}
                  </Button>
                  {canMutateArea ? (
                    <>
                      <Button onClick={() => openEdit(area)}>
                        {t['Edit']?.() ?? 'Edit'}
                      </Button>
                      <SettingsConfirmButton
                        dataTestId={`settings-teamspace-trash-${area.rootFolderId}`}
                        label={t['com.streamlain.moveToTrash.title']()}
                        confirmLabel={t['com.streamlain.moveToTrash.title']()}
                        cancelLabel={t['Cancel']()}
                        onConfirm={() => {
                          trashArea(area).catch(error => {
                            console.error('Failed to trash teamspace:', error);
                          });
                        }}
                      />
                    </>
                  ) : null}
                </SettingsActionRow>
              </SettingsExpandableItem>
            );
          })}
        </SettingsEntityList>
      )}
      <TeamspaceNameDialog
        open={modalOpen}
        onOpenChange={open => {
          if (!open) closeModal();
        }}
        title={
          editingArea
            ? (t[
                'com.streamlain.settings.workspace.teamspaces.edit-title'
              ]?.() ?? 'Edit teamspace')
            : (t[
                'com.streamlain.settings.workspace.teamspaces.create-title'
              ]?.() ?? 'New teamspace')
        }
        onSubmit={saveModal}
        onSubmitError={error => {
          console.error('Failed to save teamspace:', error);
          notify.error({
            title:
              t[
                'com.streamlain.settings.workspace.teamspaces.save-failed'
              ]?.() ?? 'Teamspace could not be saved',
          });
        }}
        initialName={
          editingArea?.name ??
          t['com.streamlain.settings.workspace.teamspaces.default-name']?.() ??
          'New teamspace'
        }
        namePlaceholder={
          t['com.streamlain.settings.workspace.teamspaces.name']?.() ?? 'Name'
        }
        saveLabel={editingArea ? t['Save']() : t['Create']()}
        cancelLabel={t['Cancel']()}
        savingLabel={
          t['com.streamlain.settings.member.loading']?.() ?? 'Loading...'
        }
        inputTestId="settings-teamspaces-modal-name-input"
        saveTestId="settings-teamspaces-modal-save-button"
      />
      {shareArea ? (
        <FolderShareModal
          folderId={shareArea.rootFolderId}
          variant="teamspace-root"
          open={!!shareArea}
          onOpenChange={open => {
            if (!open) setShareArea(null);
          }}
        />
      ) : null}
    </SettingsContentSection>
  );
};
